import { type FieldMetadata, getInputProps } from '@conform-to/react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/16/solid';
import clsx from 'clsx';
import { useState, type ReactNode } from 'react';
import { Explanation } from '#common/components/Explanation.js';
import { ErrorMessage, Field, Label } from '../catalyst-ui/fieldset';
import { Input } from '../catalyst-ui/input';

type Props = {
    inputClassName?: string;
    field: FieldMetadata<any, Record<string, any>>;
    icon: ReactNode;
    fieldClassName?: string;
    label?: ReactNode | string;
    labelClassName?: string;
    shortExplanation?: ReactNode | string;
    shortExplanationClassName?: string;
    longExplanation?: ReactNode | string;
} & Omit<Parameters<typeof Input>[0], 'name' | 'invalid' | 'className' | 'form'>;

export function FormInputFieldWithIcon({
    field,
    icon,
    fieldClassName,
    inputClassName,
    label,
    labelClassName,
    shortExplanation,
    shortExplanationClassName,
    longExplanation,
    type,
    ...inputProps
}: Props) {
    const [ showPassword, setShowPassword ] = useState(false);
    const inputType = type === 'password' ? (showPassword ? 'text' : 'password') : type ?? 'text';
    const { key, ...fieldInputProps } = getInputProps(field, { type: type ?? 'text' as any });

    const isPassword = type === 'password';

    return (
        <Field className={fieldClassName}>
            {label ? <Label className={clsx('block text-sm font-medium mb-1', labelClassName)}>{label}</Label> : null}

            <div className='flex h-full relative'>
                {<span className='p-3 border border-r-0 border-gray-300 text-gray-500 bg-gray-50'>{icon}</span>}

                {/** Check why inline styles are not working when passed as tailwind classes */}
                <Input
                    key={key}
                    {...fieldInputProps}
                    {...inputProps}
                    type={inputType}
                    invalid={!field.valid}
                    className={clsx(inputClassName, 'before:shadow-none', 'after:rounded-l-none')}
                    style={{ height: '100%', borderTopLeftRadius: '0px', borderBottomLeftRadius: '0px' }}
                />

                {isPassword && (
                    <button
                        type='button'
                        onClick={() => setShowPassword(!showPassword)}
                        className='absolute right-2 top-3 text-gray-500 focus:outline-none'
                        tabIndex={-1}
                    >
                        {showPassword ? <EyeSlashIcon className='h-5 w-5' /> : <EyeIcon className='h-5 w-5' />}
                    </button>
                )}
            </div>

            {shortExplanation || longExplanation
                ? (
                    <Explanation
                        short={shortExplanation}
                        long={longExplanation}
                        shortClassName={shortExplanationClassName}
                    />
                )
                : null}
            {field.valid ? null : field.errors?.map(x => <ErrorMessage key={x} className='mt-2'>{x}</ErrorMessage>)}
        </Field>
    );
}
